<script setup>
import { ref, computed } from "vue";
import { useStore } from "vuex";
import { onClickOutside } from "@vueuse/core";

const store = useStore();

const modal = computed(() => store.state.wishes.modal);
const balance = computed(() => store.state.wishes.balance);
const costs = computed(() => store.state.wishes.costs);
const pack = computed(() => store.state.wishes.pack);
const canBuy = computed(() => store.state.wishes.canBuy);
const buying = computed(() => store.state.wishes.buying);
const error = computed(() => store.state.wishes.error);
const price = computed(() => store.getters["wishes/priceLabel"]);

const close = () => {
  showHistory.value = false;
  showPrices.value = false;
  store.dispatch("wishes/closeModal");
};

// What things cost, shown only when asked for
const showPrices = ref(false);

// Where the wishes went
const showHistory = ref(false);
const history = computed(() => store.state.wishes.history);
const historyError = computed(() => store.state.wishes.historyError);
const openHistory = () => {
  showHistory.value = true;
  store.dispatch("wishes/fetchHistory");
};

const money = (amount, currency) => {
  try {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: String(currency).toUpperCase(),
    }).format(amount / 100);
  } catch {
    return "";
  }
};

const describe = (entry) => {
  const about = entry.prompt ? `: ${entry.prompt}` : "";
  switch (entry.reason) {
    case "welcome":
      return "Welcome wishes";
    case "gift":
      return entry.change > 0 ? "Wishes from MakeMeA" : "Wishes adjusted";
    case "make":
      return `${entry.research ? "Researched make" : "Make"}${about}`;
    case "image":
      return `${entry.edit ? "Changed a picture" : "Drew a picture"}${about}`;
    case "refund":
      return `Given back after a problem${about}`;
    case "purchase":
      return `Bought ${entry.change} wishes${
        entry.amount ? ` for ${money(entry.amount, entry.currency)}` : ""
      }`;
    default:
      return "Wishes";
  }
};

const when = (date) =>
  new Date(date).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
const buy = () => store.dispatch("wishes/buy");

const target = ref(null);
onClickOutside(target, close);

const plural = (count) => (count === 1 ? "wish" : "wishes");
const heading = computed(() => {
  if (modal.value === "empty") return "You're out of wishes";
  if (modal.value === "thanks") return "Your wishes are on their way";
  return "Your wishes";
});
</script>

<template>
  <div
    v-if="modal"
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
    role="dialog"
    aria-modal="true"
    :aria-label="heading"
    @keyup.esc="close"
  >
    <div
      ref="target"
      class="relative max-h-full w-full max-w-md overflow-y-auto rounded-2xl bg-white p-6 text-primary shadow-xl"
    >
      <button
        type="button"
        class="absolute right-4 top-4 p-2 text-secondary hover:text-primary"
        aria-label="Close"
        @click="close"
      >
        <font-awesome-icon :icon="['fas', 'circle-xmark']" class="text-xl" />
      </button>
      <img src="/logo.svg" alt="" class="mx-auto h-16 w-16" />
      <h2 class="mt-3 text-center text-2xl font-bold">{{ heading }}</h2>
      <p
        v-if="balance !== null"
        class="mt-1 flex items-center justify-center gap-2 text-secondary"
      >
        <span>
          You have
          <b class="text-accentPrimary">{{ balance }} {{ plural(balance) }}</b>
          left
        </span>
        <button
          type="button"
          class="flex h-6 w-6 items-center justify-center rounded-full text-secondary hover:bg-gray-10 hover:text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-accentPrimary"
          :class="{ 'bg-gray-10 text-primary': showPrices }"
          :aria-expanded="showPrices"
          aria-controls="wish-prices"
          title="What things cost"
          @click="showPrices = !showPrices"
        >
          <font-awesome-icon :icon="['fas', 'circle-info']" />
          <span class="sr-only">What things cost</span>
        </button>
      </p>
      <p
        v-if="modal === 'thanks'"
        class="mt-3 text-center text-sm text-secondary"
      >
        Thank you! They'll appear here as soon as your payment is confirmed.
      </p>
      <p
        v-else-if="modal === 'empty'"
        class="mt-3 text-center text-sm text-secondary"
      >
        Every make uses wishes. Top up to keep making.
      </p>

      <ul
        v-if="showPrices"
        id="wish-prices"
        class="mt-5 space-y-2 rounded-lg bg-gray-10 p-4 text-sm"
      >
        <li class="flex justify-between">
          <span>A make</span>
          <b>{{ costs.make }} {{ plural(costs.make) }}</b>
        </li>
        <li class="flex justify-between">
          <span>A make with research</span>
          <b>{{ costs.make + costs.research }} wishes</b>
        </li>
        <li class="flex justify-between">
          <span>An image</span>
          <b>{{ costs.make + costs.image }} wishes</b>
        </li>
        <li class="flex justify-between">
          <span>Another version of an image</span>
          <b>{{ costs.image }} {{ plural(costs.image) }}</b>
        </li>
        <li class="flex justify-between">
          <span>Changing a picture</span>
          <b>{{ costs.image }} {{ plural(costs.image) }}</b>
        </li>
        <li class="flex justify-between">
          <span>Editing or filling in a make</span>
          <b>Free</b>
        </li>
      </ul>

      <button
        v-if="!showHistory"
        type="button"
        class="mt-4 w-full text-center text-sm font-bold uppercase text-accentPrimary hover:text-purple-400"
        @click="openHistory"
      >
        See where your wishes went
      </button>
      <section v-else class="mt-4" aria-labelledby="wish-history-title">
        <p id="wish-history-title" class="mb-2 text-sm font-bold">
          Your wish history
        </p>
        <p v-if="history === null" class="text-sm text-secondary">Loading...</p>
        <p v-else-if="historyError" class="text-sm text-red-500">
          {{ historyError }}
        </p>
        <p v-else-if="!history.length" class="text-sm text-secondary">
          Nothing yet.
        </p>
        <ul
          v-else
          class="max-h-60 divide-y divide-gray-200 overflow-y-auto rounded-lg border border-gray-200 text-sm"
        >
          <li
            v-for="(entry, index) in history"
            :key="index"
            class="flex items-start justify-between gap-3 px-3 py-2"
          >
            <div class="min-w-0">
              <p class="[overflow-wrap:anywhere]">{{ describe(entry) }}</p>
              <p class="text-xs text-secondary">
                {{ when(entry.createdAt) }}
                <a
                  v-if="entry.receiptUrl"
                  :href="entry.receiptUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="ml-2 font-bold text-accentPrimary underline"
                  >Receipt</a
                >
              </p>
            </div>
            <b
              class="shrink-0"
              :class="entry.change > 0 ? 'text-green-600' : 'text-primary'"
              >{{ entry.change > 0 ? "+" : "−" }}{{ Math.abs(entry.change) }}</b
            >
          </li>
        </ul>
      </section>

      <button
        v-if="canBuy"
        type="button"
        class="mt-5 flex w-full items-center justify-center rounded-lg bg-accentPrimary px-5 py-3 font-bold uppercase text-white transition-colors hover:bg-purple-400 disabled:cursor-wait disabled:opacity-70"
        :disabled="buying"
        @click="buy"
      >
        {{
          buying
            ? "Taking you to checkout..."
            : `Get ${pack.wishes} wishes for ${price}`
        }}
      </button>
      <p v-else class="mt-5 text-center text-sm font-medium text-secondary">
        Buying more wishes is coming soon.
      </p>
      <p v-if="error" class="mt-2 text-center text-sm font-medium text-red-500">
        {{ error }}
      </p>
      <p v-if="canBuy" class="mt-2 text-center text-xs text-secondary">
        Secure payment by Stripe. Wishes don't expire.
      </p>
    </div>
  </div>
</template>
